import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface Window {
    __pwaDeferredPrompt?: BeforeInstallPromptEvent | null;
  }
}

const SCOOTERP_PWA_STANDALONE_KEY = "scooterp-pwa-standalone";
const SCOOTERP_PWA_SOURCE = "scooterp-pwa";

const isEmployeeManifestActive = () => {
  const manifestHref = document
    .querySelector('link[rel="manifest"]')
    ?.getAttribute("href")
    ?.toLowerCase();
  return manifestHref?.includes("employee-manifest.json") ?? false;
};

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const fromScooterpPwa = searchParams.get("source") === SCOOTERP_PWA_SOURCE;

    if (fromScooterpPwa) {
      sessionStorage.setItem(SCOOTERP_PWA_STANDALONE_KEY, "true");
    }

    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const isScooterpStandalone =
      isStandalone && sessionStorage.getItem(SCOOTERP_PWA_STANDALONE_KEY) === "true";
    setIsInstalled(isScooterpStandalone);

    // Detect iOS
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    if (window.__pwaDeferredPrompt) {
      setDeferredPrompt(window.__pwaDeferredPrompt);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      window.__pwaDeferredPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
      window.dispatchEvent(new CustomEvent("pwa:beforeinstallprompt"));
    };

    const syncHandler = () => {
      setDeferredPrompt(window.__pwaDeferredPrompt ?? null);
    };

    const installedHandler = () => {
      if (isEmployeeManifestActive()) {
        return;
      }

      sessionStorage.setItem(SCOOTERP_PWA_STANDALONE_KEY, "true");
      setIsInstalled(true);
      window.__pwaDeferredPrompt = null;
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("pwa:beforeinstallprompt", syncHandler);
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("pwa:beforeinstallprompt", syncHandler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      sessionStorage.setItem(SCOOTERP_PWA_STANDALONE_KEY, "true");
      setIsInstalled(true);
    }

    window.__pwaDeferredPrompt = null;
    setDeferredPrompt(null);
    return outcome === "accepted";
  }, [deferredPrompt]);

  return {
    canInstall: !!deferredPrompt,
    isInstalled,
    isIOS,
    install,
  };
}
